import type { ReactNode } from 'react'
import { BrokenLink } from '@/assets/icons'
import { Link } from '@/components/link/Link'
import CustomizableTooltip from '@/components/ui/CustomizableTooltip'

const kUnavailableSourceMessage = 'Source unavailable'

/** A source identity with no navigation target; focus exposes the same explanation as hover. */
export function AgentV2UnavailableSource({
  sourceUrl,
  citationId,
  children,
  forceTooltipOpen,
}: {
  sourceUrl: string
  citationId?: string
  children?: ReactNode
  /** Storybook only: show the designed hover state without pointer interaction. */
  forceTooltipOpen?: boolean
}) {
  let hostname = 'Source unavailable'
  try {
    const url = new URL(sourceUrl)
    if (['http:', 'https:'].includes(url.protocol) && !url.username && !url.password) hostname = url.hostname.replace(/^www\./, '')
  } catch {
    // A malformed or missing URL cannot provide a publisher hostname.
  }

  return (
    <CustomizableTooltip
      title={kUnavailableSourceMessage}
      describeChild
      placement="bottom"
      offset={[0, -4]}
      disableInteractive
      open={forceTooltipOpen || undefined}
      tooltipClassName="!max-w-[calc(100vw-32px)] !bg-brand-neutral-800 !text-white !font-sans !text-2xs !font-medium !leading-[10.4px] !tracking-[0.1248px] !rounded-sm !px-1 !py-0.5"
    >
      <Link
        role="link"
        aria-disabled="true"
        tabIndex={0}
        data-citation-id={citationId}
        className="group whitespace-nowrap cursor-default !text-true-gray-600 hover:!text-true-gray-700 focus-visible:!outline-true-gray-600"
      >
        {children && <span className="whitespace-normal">{children} </span>}
        {hostname}
        <span aria-hidden="true" className="ml-1 inline-flex align-middle pt-px">
          <BrokenLink aria-hidden="true" className="h-4 w-4 text-true-gray-600 group-hover:text-true-gray-700" />
        </span>
      </Link>
    </CustomizableTooltip>
  )
}
